/**
 * Firebase Analytics Utility
 * Track user events and behavior
 */

import { logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { analytics } from '../firebase/config';

/**
 * Log a custom event to Firebase Analytics
 * @param {string} eventName - Name of the event
 * @param {object} eventParams - Event parameters
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (analytics) {
    try {
      logEvent(analytics, eventName, {
        ...eventParams,
        timestamp: new Date().toISOString(),
      });
      console.log(`📊 Analytics: ${eventName}`, eventParams);
    } catch (error) {
      console.error('Analytics error:', error);
    }
  }
};

/**
 * Track page view
 * @param {string} pagePath - Page path
 * @param {string} pageTitle - Page title
 */
export const trackPageView = (pagePath, pageTitle) => {
  trackEvent('page_view', {
    page_path: pagePath,
    page_title: pageTitle,
  });
};

/**
 * Track user sign in
 * @param {string} method - Sign in method (e.g., 'google', 'email')
 */
export const trackSignIn = (method = 'unknown') => {
  trackEvent('login', {
    method: method,
  });
};

/**
 * Track user sign up
 * @param {string} method - Sign up method
 */
export const trackSignUp = (method = 'unknown') => {
  trackEvent('sign_up', {
    method: method,
  });
};

/**
 * Track card creation
 * @param {object} cardData - Card information
 */
export const trackCardCreation = (cardData = {}) => {
  trackEvent('card_created', {
    has_profile_image: !!cardData.profileImage,
    has_bio: !!cardData.bio,
    has_social_links: !!(cardData.linkedin || cardData.twitter || cardData.github),
    has_location: !!cardData.location,
  });
};

/**
 * Track card view
 * @param {string} cardId - Card username/ID
 */
export const trackCardView = (cardId) => {
  trackEvent('card_viewed', {
    card_id: cardId,
  });
};

/**
 * Track card download
 * @param {string} cardId - Card username/ID
 * @param {string} downloadType - Type of download ('image' or 'vcard')
 */
export const trackCardDownload = (cardId, downloadType = 'image') => {
  trackEvent('card_downloaded', {
    card_id: cardId,
    download_type: downloadType,
  });
};

/**
 * Track card share
 * @param {string} cardId - Card username/ID
 * @param {string} shareMethod - Share method ('native' or 'copy_link')
 */
export const trackCardShare = (cardId, shareMethod = 'copy_link') => {
  trackEvent('share', {
    content_type: 'card',
    item_id: cardId,
    method: shareMethod,
  });
};

/**
 * Track contact addition
 * @param {string} contactId - Contact card ID
 */
export const trackContactAdded = (contactId) => {
  trackEvent('contact_added', {
    contact_id: contactId,
  });
};

/**
 * Track contact deletion
 */
export const trackContactDeleted = () => {
  trackEvent('contact_deleted');
};

/**
 * Track notes added/updated
 * @param {string} contactId - Contact card ID
 */
export const trackNotesUpdated = (contactId) => {
  trackEvent('notes_updated', {
    contact_id: contactId,
  });
};

/**
 * Track search
 * @param {string} searchTerm - Search query
 */
export const trackSearch = (searchTerm) => {
  trackEvent('search', {
    search_term: searchTerm,
  });
};

/**
 * Track QR code scan
 * @param {string} cardId - Card ID that was scanned
 */
export const trackQRCodeScan = (cardId) => {
  trackEvent('qr_code_scanned', {
    card_id: cardId,
  });
};

/**
 * Track error (Crashlytics-like error tracking for web)
 * @param {string} errorMessage - Error message
 * @param {string} errorLocation - Where error occurred
 * @param {string} severity - Error severity (info, warning, error, fatal)
 * @param {object} additionalData - Additional error context
 */
export const trackError = (errorMessage, errorLocation, severity = 'error', additionalData = {}) => {
  trackEvent('exception', {
    description: errorMessage,
    fatal: severity === 'fatal',
    location: errorLocation,
    severity: severity,
    ...additionalData,
  });
  
  // Also log to console for debugging
  const logLevel = severity === 'fatal' ? 'error' : severity === 'error' ? 'error' : 'warn';
  console[logLevel](`[${severity.toUpperCase()}] ${errorLocation}: ${errorMessage}`, additionalData);
};

/**
 * Track JavaScript errors (React Error Boundaries, window.onerror)
 * @param {Error} error - JavaScript Error object
 * @param {object} errorInfo - Additional error information
 */
export const trackCrash = (error, errorInfo = {}) => {
  if (!error) return;
  
  const errorDetails = {
    message: error.message || String(error),
    stack: error.stack || 'No stack trace',
    name: error.name || 'Error',
    component: errorInfo.componentStack || errorInfo.component || 'Unknown',
    ...errorInfo,
  };
  
  trackError(
    errorDetails.message,
    errorDetails.component,
    'fatal',
    {
      error_stack: errorDetails.stack,
      error_name: errorDetails.name,
    }
  );
  
  console.error('💥 Crash tracked:', errorDetails);
};

/**
 * Track API errors
 * @param {string} endpoint - API endpoint
 * @param {number} statusCode - HTTP status code
 * @param {string} errorMessage - Error message
 * @param {object} additionalData - Additional context
 */
export const trackAPIError = (endpoint, statusCode, errorMessage, additionalData = {}) => {
  trackEvent('api_error', {
    endpoint: endpoint,
    status_code: statusCode,
    error_message: errorMessage,
    ...additionalData,
  });
  
  console.error(`API Error [${statusCode}] ${endpoint}:`, errorMessage);
};

/**
 * Set user ID for analytics
 * @param {string} userId - User ID
 */
export const setAnalyticsUserId = (userId) => {
  if (analytics && userId) {
    try {
      setUserId(analytics, userId);
      console.log('📊 Analytics: User ID set');
    } catch (error) {
      console.error('Analytics error:', error);
    }
  }
};

/**
 * Set user properties
 * @param {object} properties - User properties
 */
export const setAnalyticsUserProperties = (properties) => {
  if (analytics && properties) {
    try {
      setUserProperties(analytics, properties);
      console.log('📊 Analytics: User properties set', properties);
    } catch (error) {
      console.error('Analytics error:', error);
    }
  }
};

/**
 * Track conversion event
 * @param {string} conversionType - Type of conversion
 * @param {number} value - Value of conversion
 */
export const trackConversion = (conversionType, value = 0) => {
  trackEvent('conversion', {
    conversion_type: conversionType,
    value: value,
    currency: 'USD',
  });
};

/**
 * Track engagement time
 * @param {string} pageName - Page name
 * @param {number} timeInSeconds - Time spent on page
 */
export const trackEngagement = (pageName, timeInSeconds) => {
  trackEvent('user_engagement', {
    engagement_time_msec: timeInSeconds * 1000,
    page_name: pageName,
  });
};
